import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle, FiLock, FiLogOut, FiSend, FiX } from 'react-icons/fi';
import {
  clearClientPortalToken,
  getClientPortalMe,
  getClientPortalToken,
  getClientPurchaseOrderCatalog,
  getClientQuotationCatalog,
  submitClientPurchaseOrder,
  submitClientQuotationRequest,
} from '../api/clientPortalApi';
import CartPanel from '../components/CartPanel';
import CategoryFilterBar from '../components/CategoryFilterBar';
import ClientPortalWorkflowNav from '../components/ClientPortalWorkflowNav';
import DocumentHeader from '../components/DocumentHeader';
import InlineLoading from '../components/InlineLoading';
import Modal from '../components/Modal';
import PosProductCard from '../components/PosProductCard';
import ProductGrid from '../components/ProductGrid';
import VarietyDetailModal from '../components/VarietyDetailModal';
import VarietyGroupTabs from '../components/VarietyGroupTabs';
import {
  isResolvedProductSoldOut,
  isResolvedVarietySoldOut,
} from '../utils/productAvailability';
import { normalizeQuantity } from '../utils/quantity';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';

const formatQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const buildFormatLabel = (format) =>
  [format?.metricUnit, format?.description]
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .join(' · ') || 'Formato';

const normalizeCategoryKey = (product) => {
  const raw = product?.categoryId ?? product?.Category?.id ?? 'uncategorized';
  const normalized = String(raw || '').trim();
  return normalized || 'uncategorized';
};

const resolveCategoryLabel = (product) => {
  const category = String(product?.Category?.name || '').trim();
  if (category) return category;
  if (product?.categoryId) return `Categoria ${product.categoryId}`;
  return 'Sin categoria';
};

const withProductContext = (product, variety) => ({
  ...variety,
  productName: product?.productName,
  productImage: product?.productImage,
  Product: product,
});

const ClientQuotationRequestPage = ({ mode = 'quotation' }) => {
  const navigate = useNavigate();
  const isPurchaseOrderMode = mode === 'purchase-order';
  const pageTitle = isPurchaseOrderMode ? 'Crear orden de compra' : 'Solicitar cotización';
  const submitLabel = isPurchaseOrderMode ? 'Enviar orden de compra' : 'Enviar solicitud de cotización';
  const cartTitle = isPurchaseOrderMode ? 'Orden de compra' : 'Solicitud de cotización';
  const cartDescription = isPurchaseOrderMode
    ? 'Los totales se muestran antes de enviar.'
    : 'Los precios quedan pendientes de confirmación.';
  const notesPlaceholder = isPurchaseOrderMode
    ? 'Notas para la orden (opcional)'
    : 'Notas para la cotización (opcional)';
  const [client, setClient] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalProduct, setModalProduct] = useState(null);
  const [modalVariety, setModalVariety] = useState(null);
  const [modalFormat, setModalFormat] = useState(null);
  const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submittedRequest, setSubmittedRequest] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!getClientPortalToken()) {
        navigate('/', { replace: true });
        return;
      }
      setLoading(true);
      setError('');
      try {
        const me = await getClientPortalMe();
        if (me?.mustChangePassword) {
          navigate('/client-portal/change-password', { replace: true });
          return;
        }
        const catalogResponse = isPurchaseOrderMode
          ? await getClientPurchaseOrderCatalog()
          : await getClientQuotationCatalog();
        if (!mounted) return;
        setClient(me);
        setCatalog(Array.isArray(catalogResponse?.products) ? catalogResponse.products : []);
      } catch (requestError) {
        const code = requestError?.response?.data?.code;
        if (code === 'PASSWORD_CHANGE_REQUIRED') {
          navigate('/client-portal/change-password', { replace: true });
          return;
        }
        if (requestError?.response?.status === 401) {
          clearClientPortalToken();
          navigate('/', { replace: true });
          return;
        }
        setError(requestError?.response?.data?.message || 'No se pudo cargar el catálogo.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [isPurchaseOrderMode, navigate]);

  useEffect(() => {
    setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
  }, [modalProduct?.id]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    catalog.forEach((product) => {
      const id = normalizeCategoryKey(product);
      if (!map.has(id)) {
        map.set(id, {
          id,
          label: resolveCategoryLabel(product),
          initial: resolveCategoryLabel(product).charAt(0).toUpperCase() || '?',
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog]);

  const productMatchesSearch = (product, search) => {
    if (!search) return true;
    const productName = String(product.productName || '').toLowerCase();
    const varietyText = (product.Varieties || [])
      .map((variety) => [
        variety.varietyName,
        variety.description,
        ...(variety.VarietyPrices || []).map((format) => buildFormatLabel(format)),
      ].join(' '))
      .join(' ')
      .toLowerCase();
    return productName.includes(search) || varietyText.includes(search);
  };

  const filteredProducts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return catalog.filter((product) => {
      const categoryId = normalizeCategoryKey(product);
      if (selectedCategory !== 'all' && categoryId !== String(selectedCategory)) return false;
      return productMatchesSearch(product, search);
    });
  }, [catalog, searchTerm, selectedCategory]);

  const categoriesToRender = useMemo(() => {
    if (searchTerm.trim() || selectedCategory === 'all') return categoryOptions;
    return categoryOptions.filter((category) => String(category.id) === String(selectedCategory));
  }, [categoryOptions, searchTerm, selectedCategory]);

  const cartItems = useMemo(
    () => Object.values(cart).filter((item) => normalizeQuantity(item.quantity) > 0),
    [cart]
  );

  const cartCount = cartItems.reduce((sum, item) => sum + normalizeQuantity(item.quantity), 0);
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + Math.floor(Number(item.price || 0) * normalizeQuantity(item.quantity)),
    0
  );

  const quantities = useMemo(() => {
    const next = {};
    cartItems.forEach((item) => {
      if (!Number.isInteger(Number(item.varietyId)) || !Number.isInteger(Number(item.priceIndex))) return;
      if (!next[item.varietyId]) next[item.varietyId] = [];
      next[item.varietyId][item.priceIndex] = item.quantity;
    });
    return next;
  }, [cartItems]);

  const findSelection = useCallback((varietyId, priceIndex, varietyPriceId = null) => {
    for (const product of catalog) {
      const variety = (product.Varieties || []).find(
        (entry) => Number(entry.id) === Number(varietyId)
      );
      if (!variety) continue;
      const formats = variety.VarietyPrices || [];
      let resolvedIndex = Number.isInteger(Number(priceIndex)) ? Number(priceIndex) : -1;
      if (varietyPriceId != null) {
        const byId = formats.findIndex((format) => Number(format.id) === Number(varietyPriceId));
        if (byId >= 0) resolvedIndex = byId;
      }
      const format = formats[resolvedIndex];
      if (!format) return null;
      return {
        product,
        variety: withProductContext(product, variety),
        format,
        priceIndex: resolvedIndex,
      };
    }
    return null;
  }, [catalog]);

  const findVarietyForItem = useCallback((item) => (
    findSelection(item?.varietyId, item?.priceIndex, item?.varietyPriceId)?.variety || null
  ), [findSelection]);

  const handleLogout = () => {
    clearClientPortalToken();
    navigate('/', { replace: true });
  };

  const upsertCartItem = ({ product, variety, format, priceIndex, quantity }) => {
    if (!format?.id || format.isSoldOut) return;
    const nextQuantity = normalizeQuantity(quantity);
    if (message) setMessage('');
    if (submittedRequest) setSubmittedRequest(null);
    setCart((prev) => {
      const key = String(format.id);
      if (nextQuantity <= 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: {
          varietyId: variety.id,
          varietyPriceId: format.id,
          priceIndex,
          productName: product.productName,
          productImage: product.productImage,
          varietyName: variety.varietyName,
          varietyImage: variety.varietyImage,
          priceImage: format.priceImage,
          metricUnit: format.metricUnit,
          description: buildFormatLabel(format),
          price: Number(format.price || 0),
          subtotal: Math.floor(Number(format.price || 0) * nextQuantity),
          quantity: nextQuantity,
        },
      };
    });
  };

  const handleQuantityChange = (varietyId, priceIndex, value) => {
    const selection = findSelection(varietyId, priceIndex);
    if (!selection) return;
    upsertCartItem({ ...selection, quantity: value });
  };

  const handleQuantityIncrement = (varietyId, priceIndex, delta) => {
    const selection = findSelection(varietyId, priceIndex);
    if (!selection || selection.format?.isSoldOut) return;
    const current = normalizeQuantity(cart[String(selection.format.id)]?.quantity || 0);
    upsertCartItem({ ...selection, quantity: Math.max(current + delta, 0) });
  };

  const handleRemoveItem = (item) => {
    if (message) setMessage('');
    if (submittedRequest) setSubmittedRequest(null);
    setCart((prev) => {
      const next = { ...prev };
      delete next[String(item.varietyPriceId)];
      return next;
    });
  };

  const handleSubmit = async () => {
    setMessage('');
    setError('');
    setSubmittedRequest(null);
    if (!cartItems.length) {
      setError('Agrega al menos un producto.');
      return;
    }
    setSubmitLoading(true);
    try {
      const payloadItems = cartItems.map((item) => ({
        varietyPriceId: item.varietyPriceId,
        quantity: normalizeQuantity(item.quantity),
      }));
      const submitRequest = isPurchaseOrderMode
        ? submitClientPurchaseOrder
        : submitClientQuotationRequest;
      const response = await submitRequest({
        items: payloadItems,
        notes: notes.trim() || null,
      });
      setCart({});
      setNotes('');
      setModalProduct(null);
      setModalVariety(null);
      setModalFormat(null);
      setShowMobileCart(false);
      const requestId = isPurchaseOrderMode
        ? response?.purchaseOrder?.id
        : response?.quotationRequest?.id;
      setSubmittedRequest({
        id: requestId || null,
        type: isPurchaseOrderMode ? 'purchase-order' : 'quotation',
      });
      setMessage(`${isPurchaseOrderMode ? 'Orden de compra enviada' : 'Solicitud de cotización enviada'}. Número: #${requestId || ''}`);
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      setError(requestError?.response?.data?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const isVarietySoldOut = isResolvedVarietySoldOut;
  const isProductSoldOut = isResolvedProductSoldOut;

  const openVariety = (product, variety) => {
    if (isVarietySoldOut(variety)) return;
    setModalVariety(withProductContext(product, variety));
  };

  const handlePriceCardIncrement = (format, priceIndex) => {
    if (!modalVariety) return;
    handleQuantityIncrement(modalVariety.id, priceIndex, 1);
  };

  const handlePriceCardDecrement = (format, priceIndex) => {
    if (!modalVariety) return;
    handleQuantityIncrement(modalVariety.id, priceIndex, -1);
  };

  const handlePriceCardKeypad = (format) => {
    setModalFormat(format);
  };

  const closeModalVariety = () => {
    setModalVariety(null);
    setModalFormat(null);
  };

  const closeModalFormat = () => {
    setModalFormat(null);
  };

  const renderProductCard = (product) => {
    const imageSrc = product.productImage || 'https://placehold.co/200';
    const isSoldOut = isProductSoldOut(product);
    return (
      <PosProductCard
        key={product.id}
        productName={product.productName}
        imageSrc={imageSrc}
        badge={isSoldOut ? 'Agotado' : ''}
        onClick={() => setModalProduct(product)}
      />
    );
  };

  const renderCategoryPicker = (itemsWrapperClassName = '') => (
    <CategoryFilterBar
      categories={categoryOptions}
      selectedCategory={selectedCategory}
      onSelect={setSelectedCategory}
      includeAll
      allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
      emptyMessage="No hay categorías disponibles."
      isEmpty={categoryOptions.length === 0}
      itemsWrapperClassName={itemsWrapperClassName}
    />
  );

  const renderCartFooter = () => (
    <>
      <textarea
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder={notesPlaceholder}
        rows={3}
        className="mt-3 w-full p-3 border border-gray-300 rounded-lg resize-none"
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitLoading || !cartItems.length}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-60"
      >
        <FiSend />
        {submitLoading ? 'Enviando...' : submitLabel}
      </button>
    </>
  );

  const renderCartHeader = () => (
    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-sm font-semibold text-gray-900">{cartTitle}</div>
      <div className="mt-0.5 text-xs text-gray-600">{cartDescription}</div>
    </div>
  );

  const renderFeedbackNotice = () => {
    const text = error || message;
    if (!text) return null;
    const isSuccess = Boolean(message && !error);
    const Icon = isSuccess ? FiCheckCircle : FiAlertTriangle;
    const title = isSuccess
      ? (isPurchaseOrderMode ? 'Orden enviada' : 'Solicitud enviada')
      : 'No se pudo enviar';

    return (
      <div
        className={`mb-4 rounded-xl border bg-white px-4 py-3 shadow-sm ${
          isSuccess ? 'border-emerald-200' : 'border-red-200'
        }`}
        role={isSuccess ? 'status' : 'alert'}
      >
        <div className="flex items-start gap-3">
          <span
            className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
              isSuccess ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}
          >
            <Icon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                <p className={`mt-0.5 text-sm ${isSuccess ? 'text-emerald-700' : 'text-red-700'}`}>
                  {text}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMessage('');
                  setError('');
                }}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Cerrar aviso"
              >
                <FiX size={16} />
              </button>
            </div>
            {isSuccess && (
              <div className="mt-3 flex flex-wrap gap-2">
                {submittedRequest?.type === 'purchase-order' && (
                  <button
                    type="button"
                    onClick={() => navigate('/client-portal/purchase-orders')}
                    className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    Ver mis ordenes
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMessage('');
                    setSubmittedRequest(null);
                  }}
                  className="rounded border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Crear otra
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCartPanel = ({ mobile = false } = {}) => (
    <CartPanel
      items={cartItems}
      getVariety={findVarietyForItem}
      getItemQuantity={(item) => item.quantity}
      onItemClick={(item, variety) => {
        if (!variety) return;
        setModalVariety(variety);
      }}
      onRemoveItem={handleRemoveItem}
      canEdit
      headerContent={renderCartHeader()}
      panelClassName={mobile
        ? 'bg-white max-h-[55vh] overflow-y-auto'
        : 'bg-white shadow-lg rounded-lg p-4 md:overflow-y-auto md:max-h-[calc(86vh-210px)] md:h-[calc(86vh-210px)]'}
      footerClassName={mobile
        ? 'flex flex-col gap-1 mt-4'
        : 'flex flex-col gap-1 mt-4 bottom-0 pb-1'}
      itemsSubtotal={cartTotal}
      totalAmount={cartTotal}
      showPrices={isPurchaseOrderMode}
      showSummaryBreakdown={isPurchaseOrderMode}
      showTotal={isPurchaseOrderMode}
      footerActions={renderCartFooter()}
    />
  );

  const renderProductModal = () => {
    if (!modalProduct) return null;
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 px-4 py-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full relative flex flex-col max-h-[86vh]"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setModalProduct(null)}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
            aria-label="Cerrar"
          >
            <FiX />
          </button>
          <div className="overflow-y-auto px-2">
            <div className="flex justify-center items-center mb-4">
              <img
                src={modalProduct.productImage || 'https://placehold.co/300'}
                alt={modalProduct.productName}
                className="w-32 h-32 object-cover rounded-lg"
              />
            </div>
            <h2 className="text-xl font-bold text-center">
              Variedades de {modalProduct.productName}
            </h2>
            {(() => {
              const visibleVarieties = filterVarietiesByGroup(
                modalProduct.Varieties || [],
                modalProduct.VarietyGroups,
                activeVarietyGroupId
              );
              return (
                <>
                  <VarietyGroupTabs
                    groups={modalProduct.VarietyGroups}
                    activeGroupId={activeVarietyGroupId}
                    onSelect={setActiveVarietyGroupId}
                    className="mt-4"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {visibleVarieties.map((variety) => {
                      const soldOut = isVarietySoldOut(variety);
                      const description = String(variety.description || '').trim();
                      return (
                        <div
                          key={variety.id}
                          className={`relative bg-white border rounded-lg shadow p-4 ${
                            soldOut ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                          }`}
                          onClick={() => openVariety(modalProduct, variety)}
                        >
                          {soldOut && (
                            <span className="absolute top-2 right-2 z-10 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                              Agotado
                            </span>
                          )}
                          <div className="flex items-center gap-4 md:gap-8 mb-2">
                            <img
                              src={variety.varietyImage || modalProduct.productImage || 'https://placehold.co/200'}
                              alt={variety.varietyName || modalProduct.productName}
                              className="w-16 h-16 object-cover rounded-lg shadow"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold break-words">
                                {variety.varietyName || modalProduct.productName}
                              </p>
                              {description && (
                                <p className="mt-1 text-xs text-gray-600 break-words">
                                  {description}
                                </p>
                              )}
                            </div>
                          </div>
                          {!soldOut && (
                            <div className="text-xs text-blue-600 font-semibold text-right">
                              Ver formatos
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {visibleVarieties.length === 0 && (
                    <p className="mt-4 text-center text-sm text-gray-500">
                      No hay variedades para este grupo.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <InlineLoading label="Cargando catálogo" />
      </div>
    );
  }

  const cartWrapperClassName = 'hidden md:flex md:flex-col md:justify-end fixed top-[190px] bottom-0 app-fixed-left-pane-compact px-4 z-0';
  const mainContentClassName = 'flex-1 md:pl-[max(420px,33.333333%)] p-4 overflow-auto';

  return (
    <>
      <div className="flex flex-col min-h-screen bg-gray-100">
        <DocumentHeader
          stickyOffsetClassName="top-0"
          title={pageTitle}
          showCartButton
          onCartClick={() => setShowMobileCart(true)}
          totalAmount={isPurchaseOrderMode ? cartTotal : null}
          headerActions={
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/client-portal/change-password')}
                className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FiLock />
                Contraseña
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FiLogOut />
                Salir
              </button>
            </div>
          }
          clientName={client?.name || ''}
          clientRut={client?.rut || 'Cliente'}
          showClientType={false}
          emptyClientLabel={client?.name || 'Cliente'}
          middleContent={
            <div className="w-full max-w-[560px]">
              {renderCategoryPicker()}
            </div>
          }
          rightContent={
            <input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onFocus={(event) => event.target.select()}
              className="w-full px-3 py-2.5 text-base md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          }
        />

        <div className="relative flex">
          <div className={cartWrapperClassName}>
            {renderCartPanel()}
          </div>
        </div>

        <div className={mainContentClassName}>
          <div className="max-w-7xl mx-auto">
            <ClientPortalWorkflowNav
              activeKey={isPurchaseOrderMode ? 'purchase-order' : 'quotation'}
              onNavigate={navigate}
              className="mb-4"
            />
            {renderFeedbackNotice()}
            <div className="mb-4 lg:hidden">
              {renderCategoryPicker()}
            </div>
            <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
              <div>
                Mostrando {filteredProducts.length} resultados
              </div>
              <div className="md:hidden text-blue-700 font-semibold">
                {formatQuantity(cartCount)} en carrito
              </div>
            </div>
            <div className="space-y-8">
              {categoriesToRender.map((category) => {
                const productsInCategory = filteredProducts
                  .filter((product) => normalizeCategoryKey(product) === String(category.id))
                  .sort((a, b) => a.productName.localeCompare(b.productName));

                if (productsInCategory.length === 0) return null;

                return (
                  <div key={category.id}>
                    <div className="flex items-center gap-4 my-2">
                      <div className="flex-grow border-t border-gray-300"></div>
                      <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                        {category.label || 'Categoria'}
                      </h2>
                      <div className="flex-grow border-t border-gray-300"></div>
                    </div>

                    <ProductGrid>
                      {productsInCategory.map(renderProductCard)}
                    </ProductGrid>
                  </div>
                );
              })}
            </div>
            {!filteredProducts.length && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-600">
                No hay productos disponibles.
              </div>
            )}
          </div>
        </div>
      </div>

      {showMobileCart && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto px-4 py-6"
          onClick={() => setShowMobileCart(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <h2 className="font-bold text-gray-900">{cartTitle}</h2>
                <p className="text-xs text-gray-500">{formatQuantity(cartCount)} productos seleccionados</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileCart(false)}
                className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>
            {renderCartPanel({ mobile: true })}
          </div>
        </div>
      )}

      {renderProductModal()}

      {modalVariety && (
        <VarietyDetailModal
          variety={modalVariety}
          onClose={closeModalVariety}
          quantities={quantities}
          onPriceIncrement={handlePriceCardIncrement}
          onPriceDecrement={handlePriceCardDecrement}
          onPriceKeypad={handlePriceCardKeypad}
          onConfirmClose={closeModalVariety}
          showPrices={isPurchaseOrderMode}
        />
      )}

      {modalVariety && (
        <Modal
          isOpen={!!modalFormat}
          onClose={closeModalFormat}
          closeModalVariety={closeModalVariety}
          closeModalProduct={() => setModalProduct(null)}
          variety={modalVariety}
          edit
          quantities={quantities}
          varietyPrice={modalFormat}
          handleQuantityChange={handleQuantityChange}
          setShowDiscountModal={() => {}}
          discounts={{}}
          canDiscount={false}
          showPrices={isPurchaseOrderMode}
        />
      )}
    </>
  );
};

export default ClientQuotationRequestPage;
